import { Building2, Layers, ShieldCheck, Sparkles } from 'lucide-react'
import { useHubBranding } from '../../hooks/useHubBranding'

const HIGHLIGHTS = [
  {
    icon: ShieldCheck,
    title: 'Secure by design',
    text: 'SSO, MFA, and role-based access for every workspace.'
  },
  {
    icon: Layers,
    title: 'One control plane',
    text: 'Organizations, products, and users managed in a single hub.'
  },
  {
    icon: Sparkles,
    title: 'Ready for your teams',
    text: 'Sign in with your work email to pick up where you left off.'
  }
]

function BrandMark({ appName, logoUrl, inverted = false }) {
  if (logoUrl) {
    return (
      <div className={inverted ? 'inline-flex rounded-2xl bg-white px-3 py-2 shadow-sm' : ''}>
        <img src={logoUrl} alt={appName} className="h-8 w-auto object-contain" />
      </div>
    )
  }

  return (
    <div className="flex items-center gap-3">
      <div
        className={`flex h-10 w-10 items-center justify-center rounded-2xl ${
          inverted ? 'bg-white/10 ring-1 ring-white/20' : 'bg-primary-600 text-white'
        }`}
      >
        <Building2 className="h-5 w-5" />
      </div>
      <span className={`text-lg font-semibold tracking-tight ${inverted ? 'text-white' : 'text-secondary-900'}`}>
        {appName}
      </span>
    </div>
  )
}

export default function AuthShell({
  title,
  subtitle,
  children,
  footer,
  panelTitle,
  panelSubtitle,
  highlights,
  eyebrow
}) {
  const { branding } = useHubBranding()
  const appName = branding?.app_name || 'Product Hub'
  const logoUrl = branding?.logo_url
  const primary = branding?.primary_color || '#2563EB'
  const secondary = branding?.secondary_color || '#64748B'
  const panelItems = highlights || HIGHLIGHTS

  return (
    <div
      className="min-h-screen lg:grid lg:grid-cols-2"
      style={{ '--hub-primary': primary, '--hub-secondary': secondary }}
    >
      <aside
        className="relative hidden overflow-hidden lg:flex lg:flex-col lg:justify-between px-12 py-12 text-white"
        style={{
          background: `linear-gradient(165deg, #0b1220 0%, #152033 42%, ${primary} 165%)`
        }}
      >
        <div
          className="pointer-events-none absolute -right-16 -top-24 h-80 w-80 rounded-full blur-3xl"
          style={{ background: primary, opacity: 0.35 }}
        />
        <div
          className="pointer-events-none absolute -bottom-16 -left-10 h-72 w-72 rounded-full blur-3xl"
          style={{ background: secondary, opacity: 0.28 }}
        />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
            backgroundSize: '28px 28px'
          }}
        />

        <div className="relative z-10">
          <BrandMark appName={appName} logoUrl={logoUrl} inverted />
        </div>

        <div className="relative z-10 max-w-md">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-white/55">
            {eyebrow || appName}
          </p>
          <h1 className="mt-3 text-4xl font-semibold leading-[1.15] tracking-tight">
            {panelTitle || (
              <>
                Your products.
                <br />
                One identity.
              </>
            )}
          </h1>
          <p className="mt-4 text-[15px] leading-relaxed text-white/70">
            {panelSubtitle ||
              'Sign in to manage organizations, subscriptions, and access across the platform.'}
          </p>

          <ul className="mt-10 space-y-4">
            {panelItems.map(({ icon: Icon, title: itemTitle, text }) => (
              <li key={itemTitle} className="flex gap-3.5">
                <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/15">
                  <Icon className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-medium">{itemTitle}</p>
                  <p className="text-sm text-white/65">{text}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative z-10 text-xs text-white/40">
          © {new Date().getFullYear()} {appName}
        </p>
      </aside>

      <main className="relative flex min-h-screen items-center justify-center bg-secondary-50 px-5 py-10 sm:px-8">
        <div
          className="pointer-events-none absolute inset-0 opacity-80"
          style={{
            background:
              'radial-gradient(900px 420px at 80% -10%, color-mix(in srgb, var(--hub-primary) 14%, transparent), transparent 60%)'
          }}
        />
        <div className="relative z-10 w-full max-w-[420px]">
          <div className="mb-8 lg:hidden">
            <BrandMark appName={appName} logoUrl={logoUrl} />
          </div>

          {(title || subtitle) && (
            <div className="mb-8">
              {title && (
                <h2 className="text-[1.65rem] font-semibold tracking-tight text-secondary-900">
                  {title}
                </h2>
              )}
              {subtitle && (
                <p className="mt-2 text-sm leading-relaxed text-secondary-500">{subtitle}</p>
              )}
            </div>
          )}

          {children}

          {footer && <div className="mt-8">{footer}</div>}
        </div>
      </main>
    </div>
  )
}
